import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import type { WebMcpTool } from "@/lib/webmcp/tools";

/**
 * Minimal shape of the W3C Web Model Context API we rely on, so we don't depend
 * on the polyfill's global type augmentation being picked up by the compiler.
 */
interface ModelContextLike {
  registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => void;
}

/**
 * WebMcpProvider
 *
 * Registers this site's real actions as in-browser WebMCP tools via
 * `navigator.modelContext`, so in-page AI agents (and the MCP-B browser
 * extension) can discover and run them. Tools mirror the server-side MCP
 * endpoint but add browser-native actions like SPA navigation.
 *
 * Renders nothing.
 */
export function WebMcpProvider() {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const controller = new AbortController();
    let cancelled = false;

    const register = async () => {
      try {
        // Install navigator.modelContext if the browser doesn't provide it.
        const { initializeWebModelContext } = await import("@mcp-b/global");
        initializeWebModelContext();

        if (cancelled) return;

        const modelContext = (
          navigator as unknown as { modelContext?: ModelContextLike }
        ).modelContext;
        if (!modelContext?.registerTool) return;

        const navigate = (path: string) => {
          void router.navigate({ to: path as never });
        };

        // Dynamically import the tools module so zod + zod-to-json-schema and
        // the catalog stay out of the eager main bundle (FCP win).
        const { createWebMcpTools } = await import("@/lib/webmcp/tools");
        if (cancelled) return;

        for (const tool of createWebMcpTools({ navigate })) {
          modelContext.registerTool(tool, { signal: controller.signal });
        }
      } catch {
        // WebMCP is progressive enhancement — ignore unsupported environments.
      }
    };

    // Defer registration until the browser is idle so it never competes with
    // first paint / hydration. Falls back to a short timeout where idle
    // callbacks are unavailable.
    const win = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (typeof win.requestIdleCallback === "function") {
      idleId = win.requestIdleCallback(() => void register(), { timeout: 3000 });
    } else {
      timerId = setTimeout(() => void register(), 1500);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) clearTimeout(timerId);
      // Aborting the shared signal unregisters every tool we added.
      controller.abort();
    };
  }, [router]);

  return null;
}
